import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { servicesData } from '../data/servicesData';
import { caseStudiesData, caseStudyAliases } from '../data/caseStudiesData';
import { quickAccessLinks } from '../data/quickPagesData';
import { footerLocations } from '../data/contactInfo';
import WhatsAppLink from './Shared/WhatsAppLink';
import { appServicesCatalog } from '../data/appServicesCatalog';
import letsTalkChatIcon from '../assets/lets-talk-chat.svg';

const headerLogo = process.env.PUBLIC_URL + '/images/appivorous-mark-light.svg';



const navLinks = [
  { label: 'About Us', href: '/about-us', activePaths: ['/about-us', '/about_us.php'] },
  { label: 'Services', action: 'services' },
  { label: 'Our Process', href: '/our-process', activePaths: ['/our-process'] },
  { label: 'Case Studies', href: '/case-study.php', activePaths: ['/case-study', '/case-study.php', ...caseStudyAliases] },
];

// eslint-disable-next-line no-unused-vars
const serviceOverlayLabels = [
  ['graphics-design', 'Graphics', 'Designing'],
  ['animation', '2D/3D', 'Animation'],
  ['web-development', 'Web', 'Development'],
  ['digital-marketing', 'Digital', 'Marketing'],
  ['mobile-application', 'Mobile', 'Application'],
  ['blockchain-development', 'Blockchain', 'Dev'],
  ['devops', 'Dev', 'Ops'],
];

const quickLinks = quickAccessLinks
  .filter(([label]) => label !== 'Blog')
  .map(([label, href]) => ({ label, href }));

const caseStudies = caseStudiesData.map((study) => ({
  label: study.menuLabel,
  href: study.path,
}));

function isInternal(href) {
  return href.startsWith('/');
}

function SmartLink({ href, children, className, onClick, ariaLabel }) {
  if (isInternal(href)) {
    return <Link className={className} to={href} onClick={onClick} aria-label={ariaLabel}>{children}</Link>;
  }

  return <a className={className} href={href} onClick={onClick} aria-label={ariaLabel}>{children}</a>;
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  const servicesCloseTimer = useRef(null);
  const location = useLocation();
  const path = location.pathname.replace(/\/$/, '') || '/';
  const isHomePage = path === '/';
  const isServicesIndex = path === '/services';
  const servicesActive = path === '/services' || servicesData.some((service) => service.path === path || service.legacyPath === path) || appServicesCatalog.some((service) => service.path === path);
  const isActiveLink = (link) => (link.activePaths || [link.href]).includes(path);
  const serviceItems = appServicesCatalog
    .filter((service) => !['web-application-development', 'startup-app-development'].includes(service.slug))
    .map((service) => ({ label: service.title, href: service.path }));

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;

    const scrollY = window.scrollY;
    const body = document.body;
    const root = document.documentElement;
    const previousBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    const previousRootOverflow = root.style.overflow;

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    root.style.overflow = 'hidden';

    return () => {
      body.style.position = previousBodyStyles.position;
      body.style.top = previousBodyStyles.top;
      body.style.width = previousBodyStyles.width;
      body.style.overflow = previousBodyStyles.overflow;
      root.style.overflow = previousRootOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  useEffect(() => {
    let previousY = window.scrollY;

    const updateHeaderState = () => {
      const currentY = window.scrollY;
      const movingDown = currentY > previousY + 6;
      const movingUp = currentY < previousY - 6;

      setIsScrolled(currentY > 18);

      if (currentY < 42 || open || servicesOpen) {
        setHideHeader(false);
      } else if (movingDown) {
        setHideHeader(true);
      } else if (movingUp) {
        setHideHeader(false);
      }

      previousY = currentY;
    };

    updateHeaderState();
    window.addEventListener('scroll', updateHeaderState, { passive: true });

    return () => window.removeEventListener('scroll', updateHeaderState);
  }, [open, servicesOpen]);

  const showServices = () => {
    window.clearTimeout(servicesCloseTimer.current);
    setOpen(false);
    setServicesOpen(true);
  };

  const scheduleServicesClose = () => {
    window.clearTimeout(servicesCloseTimer.current);
    servicesCloseTimer.current = window.setTimeout(() => setServicesOpen(false), 180);
  };

  const toggleMenu = () => {
    setServicesOpen(false);
    setOpen((value) => !value);
  };

  return (
    <>
      <header className={`site-header ${isHomePage ? 'is-home-header' : 'is-inner-header'} ${servicesActive ? 'is-services-header' : ''} ${isServicesIndex ? 'is-services-index-header' : ''} ${open ? 'menu-open' : ''} ${isScrolled ? 'is-scrolled' : ''} ${hideHeader && !open && !servicesOpen ? 'is-hidden' : ''}`}>
        <div className="container1 header-grid">
          {!isHomePage && (
            <Link className="logo-left" to="/" aria-label="Appivorous home">
              <img src={headerLogo} alt="Appivorous" />
            </Link>
          )}

          <nav className="right-nav" aria-label="Primary navigation">
            <ul>
              {navLinks.map((link) => (
                <li
                  key={link.label}
                  className={link.action === 'services' ? 'services-nav-item' : undefined}
                  onMouseEnter={link.action === 'services' ? showServices : undefined}
                  onMouseLeave={link.action === 'services' ? scheduleServicesClose : undefined}
                  onBlur={link.action === 'services' ? (event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) setServicesOpen(false);
                  } : undefined}
                >
                  {link.action === 'services' ? (
                    <>
                    <Link className={`nav-link-button ${servicesOpen || servicesActive ? 'active' : ''}`} to="/services" aria-expanded={servicesOpen} aria-controls="services-dropdown" onFocus={showServices}>
                      {link.label}<span className="services-chevron" aria-hidden="true" />
                    </Link>
                    <div id="services-dropdown" className={`services-hover-dropdown ${servicesOpen ? 'is-open' : ''}`} aria-hidden={!servicesOpen} onMouseEnter={showServices}>
                      <ul>
                        {serviceItems.map((item) => (
                          <li key={item.href}><SmartLink href={item.href} onClick={() => setServicesOpen(false)}>{item.label}</SmartLink></li>
                        ))}
                      </ul>
                    </div>
                    </>
                  ) : (
                    <SmartLink className={isActiveLink(link) ? 'active' : ''} href={link.href}>{link.label}</SmartLink>
                  )}
                </li>
              ))}
              <li><Link className="header-talk-button" to="/connect"><img className="header-talk-icon" src={letsTalkChatIcon} alt="" aria-hidden="true" />Let's Talk</Link></li>
              <li>
                <button className="menu-button" type="button" aria-label={open ? 'Close menu' : 'Open menu'} onClick={toggleMenu}>
                  <span /><span /><span />
                </button>
              </li>
            </ul>
          </nav>

          <button className="menu-button mobile-menu" type="button" aria-label={open ? 'Close menu' : 'Open menu'} onClick={toggleMenu}>
            <span /><span /><span />
          </button>
        </div>
      </header>

      <section className={`mega-menu ${open ? 'is-open' : ''}`} aria-hidden={!open}>
        <div className="container1 mega-grid">
          <div className="mega-brand"><Link to="/">Appivorous</Link></div>
          <div className="mega-columns">
            <div className="mega-about">
              <span>About Us</span>
              <p><strong>At APPIVOROUS,</strong> we develop innovative, creative products and services that provide significant communication and information solutions.</p>
            </div>
            <MenuColumn title="Services" items={serviceItems} />
            <MenuColumn title="Quick Access Links" items={quickLinks} />
            <MenuColumn title="Case Studies" items={caseStudies} />
          </div>
          <div className="menu-line" />
          <div className="address-grid">
            {footerLocations
              .filter((location) => location.country === 'United States')
              .map((location) => <Address key={location.country} location={location} />)}
          </div>
        </div>
      </section>

    </>
  );
}

function MenuColumn({ title, items }) {
  return (
    <div className="menu-column">
      <span>{title}</span>
      <ul>{items.map((item) => <li key={item.label}><SmartLink href={item.href}>{item.label}</SmartLink></li>)}</ul>
    </div>
  );
}

function Address({ location }) {
  return (
    <div className="address-box">
      <span>{location.country}</span>
      <small>Registered Office</small>
      {location.address && <p>{location.address}</p>}
      <div className="sales-box">
        <a href="mailto:hello@appivorous.com">hello@appivorous.com</a>
        <a href="mailto:hello@appivorous.com">hello@appivorous.com</a>
        <WhatsAppLink />
      </div>
    </div>
  );
}

export default Navbar;
